import { Avatar, Badge, Dropdown, Button, Image, Modal, Space, Tag, Tooltip, Typography, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  CopyOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MoreOutlined,
  PlayCircleFilled,
  SyncOutlined,
} from '@ant-design/icons';
import type { NavigateFunction } from 'react-router-dom';
import { ProductState, ProductSaleType, TrackingMode } from '@tcr-erp/shared';
import type { ProductTenantType } from '@tcr-erp/shared';
import type { ERPColumnDef } from '@/components/DataTable/ERPTable';
import { ROUTES } from '@/constants/routes';
import { PRODUCT_STATE_LABEL, PRODUCT_STATE_BADGE_STATUS } from './constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ProductListItem extends ProductTenantType {
  variants?: Array<{ _id: string; variant_code: string }>;
}

export interface BuildColumnsParams {
  /** Map of category _id → display name */
  categoryMap: Map<string, string>;
  /** Clone mutation trigger */
  onClone: (record: ProductListItem) => void;
  /** Ẩn sản phẩm (state → INACTIVE) */
  onDelete: (id: string, name: string) => void;
  /** TCR-1840 — Hiện lại sản phẩm đang ẩn (state → ACTIVE) */
  onActivate: (id: string, name: string) => void;
  navigate: NavigateFunction;
  /** Tắt → bỏ hẳn cột "Quản lý tồn kho" (không chỉ ẩn — cũng không xuất hiện
   *  trong panel Cài đặt cột). */
  serialTrackingEnabled: boolean;
  /** TCR-1837 — gate menu "..." theo quyền. "Xem trước" luôn hiện (đã đứng được ở màn list). */
  canUpdate: boolean;
  canCreate: boolean;
  canDelete: boolean;
}

// ─── Action menu (3-dot dropdown) — tách riêng để test độc lập, không cần render Dropdown ────

export interface ProductActionMenuArgs {
  onClone: (record: ProductListItem) => void;
  onDelete: (id: string, name: string) => void;
  onActivate: (id: string, name: string) => void;
  navigate: NavigateFunction;
  canUpdate: boolean;
  canCreate: boolean;
  canDelete: boolean;
  record?: ProductListItem;
}

/** TCR-1837 — chỉ products.view: chỉ còn "Xem trước". Sửa/Đồng bộ MISA cần products.update,
 *  Nhân bản cần products.create, Ẩn cần products.delete. */
export function buildProductActionMenuItems({
  onClone,
  onDelete,
  onActivate,
  navigate,
  canUpdate,
  canCreate,
  canDelete,
  record,
}: ProductActionMenuArgs): MenuProps['items'] {
  const items: NonNullable<MenuProps['items']> = [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'Xem trước',
      onClick: () => record && navigate(ROUTES.products.detail(record._id)),
    },
  ];

  if (canUpdate) {
    items.push({
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Sửa',
      onClick: () => record && navigate(ROUTES.products.edit(record._id)),
    });
  }

  if (canCreate) {
    items.push({
      key: 'clone',
      icon: <CopyOutlined />,
      label: 'Nhân bản',
      onClick: () => {
        if (!record) return;
        Modal.confirm({
          title: 'Nhân bản sản phẩm',
          content: `Bạn muốn tạo bản sao của sản phẩm "${record.name}"?`,
          okText: 'Nhân bản',
          cancelText: 'Huỷ',
          onOk: () => onClone(record),
        });
      },
    });
  }

  if (canUpdate) {
    items.push({
      key: 'misa',
      icon: <SyncOutlined />,
      label: 'Đồng bộ MISA',
      onClick: () => {
        void message.info('Tính năng đang phát triển');
      },
    });
  }

  if (canDelete) {
    items.push({ type: 'divider' });
    // TCR-1839/TCR-1840 — item toggle theo trạng thái hiện tại của sản phẩm: đang hiển thị/nháp
    // thì cho ẩn (icon con mắt gạch chéo), đang ẩn (INACTIVE) thì đổi thành "Hiện" (icon con mắt).
    items.push(
      record?.state === ProductState.INACTIVE
        ? {
            key: 'activate',
            icon: <EyeOutlined />,
            label: 'Hiện',
            onClick: () => record && onActivate(record._id, record.name),
          }
        : {
            key: 'delete',
            icon: <EyeInvisibleOutlined />,
            label: 'Ẩn',
            danger: true,
            onClick: () => record && onDelete(record._id, record.name),
          },
    );
  }

  return items;
}

// ─── Column builder ───────────────────────────────────────────────────────────

export function buildProductColumns(params: BuildColumnsParams): ERPColumnDef<ProductListItem>[] {
  const { categoryMap, onClone, onDelete, onActivate, navigate, serialTrackingEnabled, canUpdate, canCreate, canDelete } =
    params;

  const columns: ERPColumnDef<ProductListItem>[] = [
    // 1. Ảnh
    {
      key: 'medias',
      title: 'Ảnh',
      width: 80,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const primary = record.medias?.[0];
        const src = primary?.thumbnail || primary?.url;
        if (src) {
          return (
            <div style={{ position: 'relative', width: 48, height: 48 }}>
              <Image
                src={src}
                width={48}
                height={48}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={false}
                fallback="data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' fill='%23f5f5f5'/%3E%3C/svg%3E"
              />
              {primary?.type === 'video' && (
                <PlayCircleFilled
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    left: 2,
                    color: 'rgba(255,255,255,0.95)',
                    fontSize: 16,
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          );
        }
        return (
          <Avatar shape="square" size={48} style={{ background: '#a4193d' }}>
            {record.name?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
        );
      },
    },

    // 2. Mã SP (clickable → detail + copyable) — EG-1691
    {
      key: 'sku',
      dataIndex: 'sku',
      title: 'Mã SP',
      width: 200,
      alwaysVisible: true,
      render: (sku: string, record: ProductListItem) => (
        <Space size={4}>
          <Typography.Link
            onClick={() => navigate(ROUTES.products.detail(record._id))}
            style={{ fontWeight: 500 }}
          >
            {sku ?? '—'}
          </Typography.Link>
          {sku && (
            <Typography.Text copyable={{ text: sku, tooltips: ['Sao chép', 'Đã sao chép'] }} />
          )}
        </Space>
      ),
    },

    // 3. Tên + tên ngắn
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Tên sản phẩm',
      width: 300,
      alwaysVisible: true,
      render: (name: string, record: ProductListItem) => (
        <div>
          <div>{name}</div>
          {record.short_name && (
            <div style={{ fontSize: 12, color: '#6b6b6b' }}>{record.short_name}</div>
          )}
        </div>
      ),
    },

    // 4. Danh mục
    {
      key: 'category_id',
      title: 'Danh mục',
      width: 140,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const ids: string[] = Array.isArray(record.category_id) ? record.category_id : [];
        if (ids.length === 0) return <span style={{ color: '#6b6b6b' }}>—</span>;
        const visible = ids.slice(0, 2);
        const hidden = ids.slice(2);
        return (
          <Space size={4} wrap>
            {visible.map((id) => (
              <Tag key={id}>{categoryMap.get(id) ?? id}</Tag>
            ))}
            {hidden.length > 0 && (
              <Tooltip title={hidden.map((id) => categoryMap.get(id) ?? id).join(', ')}>
                <Tag>+{hidden.length}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },

    // 5. Hình thức bán
    {
      key: 'sale_type',
      title: 'Hình thức bán',
      width: 120,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const types: ProductSaleType[] = Array.isArray(record.sale_type)
          ? record.sale_type
          : record.sale_type
            ? [record.sale_type]
            : [];
        const labelMap: Record<ProductSaleType, string> = {
          [ProductSaleType.ONLINE]: 'Online',
          [ProductSaleType.STORE]: 'Tại chi nhánh',
        };
        if (types.length === 0) return <span style={{ color: '#6b6b6b' }}>—</span>;
        return (
          <Space size={4} wrap>
            {types.map((t) => (
              <Tag key={t}>{labelMap[t] ?? t}</Tag>
            ))}
          </Space>
        );
      },
    },

    // 6. Quản lý tồn kho
    {
      key: 'tracking_mode',
      title: 'Quản lý tồn kho',
      width: 130,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        if (record.tracking_mode === TrackingMode.SERIAL) {
          return <Tag color="red">Mã quy cách</Tag>;
        }
        if (record.tracking_mode === TrackingMode.QUANTITY) {
          return <Tag color="gold">Số lượng</Tag>;
        }
        return <span style={{ color: '#6b6b6b' }}>—</span>;
      },
    },

    // 7. Biến thể
    {
      key: 'variants',
      title: 'Biến thể',
      width: 220,
      hideable: true,
      render: (_: unknown, record: ProductListItem) => {
        const variants = record.variants ?? [];
        if (variants.length === 0) return <span style={{ color: '#6b6b6b' }}>—</span>;
        const visible = variants.slice(0, 3);
        const hidden = variants.slice(3);
        return (
          <Space size={4} wrap>
            {visible.map((v) => (
              <Tag key={v._id}>{v.variant_code}</Tag>
            ))}
            {hidden.length > 0 && (
              <Tooltip title={hidden.map((v) => v.variant_code).join(', ')}>
                <Tag>+{hidden.length}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },

    // 8. Trạng thái
    {
      key: 'state',
      dataIndex: 'state',
      title: 'Trạng thái',
      width: 100,
      alwaysVisible: true,
      render: (state: ProductState) => (
        <Badge
          status={PRODUCT_STATE_BADGE_STATUS[state] ?? 'default'}
          text={PRODUCT_STATE_LABEL[state] ?? 'Không xác định'}
        />
      ),
    },

    // 9. Actions (3-dot dropdown)
    {
      key: '__action',
      title: '',
      width: 60,
      fixed: 'right' as const,
      alwaysVisible: true,
      render: (_: unknown, record: ProductListItem) => {
        const items = buildProductActionMenuItems({
          onClone,
          onDelete,
          onActivate,
          navigate,
          canUpdate,
          canCreate,
          canDelete,
          record,
        });
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} size="small" aria-label="Thao tác" />
          </Dropdown>
        );
      },
    },
  ];

  return serialTrackingEnabled ? columns : columns.filter((c) => c.key !== 'tracking_mode');
}

// ─── Backward-compat static export (used by index.tsx until migration) ────────

/** @deprecated Use buildProductColumns() instead. */
export const productColumns: ERPColumnDef<ProductTenantType>[] = buildProductColumns({
  categoryMap: new Map(),
  onClone: () => undefined,
  onDelete: () => undefined,
  onActivate: () => undefined,
  navigate: () => undefined,
  serialTrackingEnabled: true,
  canUpdate: true,
  canCreate: true,
  canDelete: true,
});

export const PRODUCT_TABLE_KEY = 'products-list-v2';
